import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  TrendingUp, 
  Shield, 
  BarChart3, 
  Smartphone, 
  Cloud
} from 'lucide-react';

const Features: React.FC = () => {
  const features = [
    {
      icon: Target,
      title: "Smart Goal Setting",
      description: "Set personalized financial goals with realistic timelines and milestones. Track your progress with visual indicators.",
      color: "primary"
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Monitor your savings and spending patterns with intuitive charts and real-time progress updates.",
      color: "success"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your financial data is encrypted and stored securely. We prioritize your privacy and data protection.",
      color: "info"
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description: "Get detailed insights into your spending habits and receive personalized recommendations.",
      color: "accent"
    },
    {
      icon: Smartphone,
      title: "Cross-Platform Sync",
      description: "Access your goals and progress from any device. Seamless synchronization across all platforms.",
      color: "primary"
    },
    {
      icon: Cloud,
      title: "Cloud Backup",
      description: "Never lose your data with automatic cloud backup. Your goals are safe and always accessible.",
      color: "success"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="features" className="py-20 bg-grey-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-textBase mb-6">
            Powerful Features for
            <span className="gradient-text"> Goal Achievement</span>
          </h2>
          <p className="text-xl text-grey-600 max-w-3xl mx-auto">
            Everything you need to set, track, and achieve your financial goals. 
            Our comprehensive feature set makes Financial Goal Tracker simple and effective.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={`bg-white rounded-brand-lg p-6 shadow-brand hover:shadow-brand-lg transition-all duration-300 group ${
                index === 4 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              <div className={`w-12 h-12 rounded-lg bg-${feature.color}/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className={`w-6 h-6 text-${feature.color}`} />
              </div>
              <h3 className="text-xl font-semibold text-textBase mb-3">
                {feature.title}
              </h3>
              <p className="text-grey-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-white rounded-brand-lg p-8 shadow-brand max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-textBase mb-4">
              Ready to Start Your Journey?
            </h3>
            <p className="text-grey-600 mb-6">
              Join thousands of users who are already achieving their financial goals with our app.
            </p>
            <motion.button
              className="btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Today
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features; 